import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { countryNameFor } from '../../util/custom/countryNames';
import {
  getShippingConfig,
  allowsMultipleItems,
  isSubunitAmount,
} from '../../util/custom/shipping';

import css from './ShippingInfo.module.css';

const { Money } = sdkTypes;

// getCountryCodes(locale) rebuilds a ~254-entry array on every call, but its
// output only depends on locale — cache the code→name lookup per locale once
// at module scope instead of recomputing it on every render (this component
// can render twice per listing page once OrderPanel also renders it).
// Moved to util/custom/countryNames.js when a third caller appeared — this one,
// StockLocationSelect and EditListingDeliveryForm. Same cache, same behaviour.

/**
 * Buyer-facing shipping information (CL-SHIP-01).
 *
 * Display only. The amount actually charged is calculated server-side in a
 * privileged transition — never reuse this component's maths for that.
 *
 * @param {Object} props
 * @param {Object} props.publicData - listing publicData
 * @param {Object} props.listingTypeConfig - the listing type config, for the stock type
 * @param {string} props.marketplaceCurrency - e.g. 'USD'
 * @param {Object} props.intl - react-intl instance
 * @param {'full'|'compact'} [props.variant='full']
 */
const ShippingInfo = props => {
  const {
    className,
    rootClassName,
    publicData,
    listingTypeConfig,
    marketplaceCurrency,
    intl,
    variant = 'full',
  } = props;

  const shippingConfig = getShippingConfig(publicData);
  if (!shippingConfig) return null;

  const currency = shippingConfig.currency || marketplaceCurrency;
  const isCompact = variant === 'compact';
  const showAdditional = allowsMultipleItems(listingTypeConfig);

  const money = amount => formatMoney(intl, new Money(amount, currency));

  const domesticFirst = shippingConfig.domestic?.firstItem;
  const internationalEnabled = !!shippingConfig.international?.enabled;
  const internationalFirst = shippingConfig.international?.firstItem;

  const countryName = countryNameFor(shippingConfig.origin?.countryCode, intl.locale);
  const region = shippingConfig.origin?.region;

  const classes = classNames(rootClassName || css.root, className, {
    [css.compact]: isCompact,
  });

  return (
    <section className={classes}>
      {!isCompact ? (
        <h2 className={css.heading}>
          <FormattedMessage id="ShippingInfo.heading" />
        </h2>
      ) : null}

      {isSubunitAmount(domesticFirst) ? (
        <p className={css.priceLine}>
          {domesticFirst === 0 ? (
            countryName ? (
              <FormattedMessage id="ShippingInfo.domesticFree" values={{ country: countryName }} />
            ) : (
              <FormattedMessage id="ShippingInfo.domesticFreeNoCountry" />
            )
          ) : countryName ? (
            <FormattedMessage
              id="ShippingInfo.domesticPrice"
              values={{ amount: money(domesticFirst), country: countryName }}
            />
          ) : (
            <FormattedMessage
              id="ShippingInfo.domesticPriceNoCountry"
              values={{ amount: money(domesticFirst) }}
            />
          )}
        </p>
      ) : null}

      {/*
        Without a known origin country (legacy listings predating this feature —
        see getShippingConfig's fallback), we cannot claim an international
        policy either way. Showing "domesticOnly" here would fabricate one, so
        this whole section stays silent instead.
      */}
      {countryName && internationalEnabled && isSubunitAmount(internationalFirst) ? (
        <p className={css.priceLine}>
          {internationalFirst === 0 ? (
            <FormattedMessage id="ShippingInfo.internationalFree" />
          ) : (
            <FormattedMessage
              id="ShippingInfo.internationalPrice"
              values={{ amount: money(internationalFirst) }}
            />
          )}
        </p>
      ) : null}

      {/*
        "domesticOnly" is a policy claim, so it may only render when the data
        actually says international.enabled === false. A stored
        international.enabled: true with no valid amount (draft data, an
        operator/Integration-API edit) must render neither this statement nor
        an international price — the publish gate keeps this unreachable for
        published listings, but drafts and API edits can still reach it.
      */}
      {countryName && !internationalEnabled ? (
        <p className={css.priceLine}>
          <FormattedMessage id="ShippingInfo.domesticOnly" values={{ country: countryName }} />
        </p>
      ) : null}

      {showAdditional && isSubunitAmount(shippingConfig.domestic?.additionalItem) ? (
        <p className={css.detailLine}>
          <FormattedMessage
            id="ShippingInfo.additionalItemsDomestic"
            values={{ amount: money(shippingConfig.domestic.additionalItem) }}
          />
          {internationalEnabled && isSubunitAmount(shippingConfig.international?.additionalItem) ? (
            <>
              {' · '}
              <FormattedMessage
                id="ShippingInfo.additionalItemsInternational"
                values={{ amount: money(shippingConfig.international.additionalItem) }}
              />
            </>
          ) : null}
        </p>
      ) : null}

      {countryName ? (
        <p className={css.soldFrom}>
          {region ? (
            <FormattedMessage
              id="ShippingInfo.soldFromWithRegion"
              values={{ region, country: countryName }}
            />
          ) : (
            <FormattedMessage id="ShippingInfo.soldFrom" values={{ country: countryName }} />
          )}
        </p>
      ) : null}

      {!isCompact ? (
        <>
          {Number.isInteger(shippingConfig.handlingTimeDays) ? (
            <p className={css.detailLine}>
              <FormattedMessage
                id="ShippingInfo.handlingTime"
                values={{ days: shippingConfig.handlingTimeDays }}
              />
            </p>
          ) : null}

          {shippingConfig.notes ? (
            <p className={css.detailLine}>
              <FormattedMessage id="ShippingInfo.notes" values={{ notes: shippingConfig.notes }} />
            </p>
          ) : null}

          {internationalEnabled ? (
            <p className={css.finePrint}>
              <FormattedMessage id="ShippingInfo.customsNote" />
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default ShippingInfo;
